//src/components/EditPropertyContext.jsx
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axiosInstance from '../axios';
import { buildUpdatePayload, formatRupiahInput, validateStep } from './EditProperty.Utils';
import { createEmptyNearbyPlace, initialForm, STEPS } from './sellProperty.constants';

const EditPropertyContext = createContext(null);

export const useEditProperty = () => {
    const ctx = useContext(EditPropertyContext);
    if (!ctx) {
        throw new Error('useEditProperty must be used inside EditPropertyProvider');
    }
    return ctx;
};

export const EditPropertyProvider = ({ children }) => {
    const navigate = useNavigate();
    const { identifier } = useParams();

    const [step, setStep] = useState(0);
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingProperty, setLoadingProperty] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [property, setProperty] = useState(null);

    const [propertyTypes, setPropertyTypes] = useState([]);
    const [certificateTypes, setCertificateTypes] = useState([]);
    const [featureGroups, setFeatureGroups] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    const [propertyImages, setPropertyImages] = useState([]);
    const [imageError, setImageError] = useState('');

    const setFieldValue = useCallback((name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    }, []);


    useEffect(() => {
        const fetchRefData = async () => {
            try {
                const [typesRes, certRes, groupsRes] = await Promise.all([
                    axiosInstance.get('/master-data/property-types'),
                    axiosInstance.get('/master-data/property-certificate-types'),
                    axiosInstance.get('/master-data/property-feature-groups'),
                ]);

                if (typesRes.data?.success) {
                    setPropertyTypes(Array.isArray(typesRes.data.data) ? typesRes.data.data : []);
                }

                if (certRes.data?.success) {
                    setCertificateTypes(Array.isArray(certRes.data.data) ? certRes.data.data : []);
                }

                const groups = Array.isArray(groupsRes.data?.data) ? groupsRes.data.data : [];

                const groupsWithFeatures = await Promise.all(
                    groups.map(async (group) => {
                        try {
                            const featuresRes = await axiosInstance.get(
                                `/master-data/property-feature-groups/${group.code}/features`
                            );
                            const features = Array.isArray(featuresRes.data?.data?.features)
                                ? featuresRes.data.data.features
                                : [];
                            return { ...group, features };
                        } catch {
                            return { ...group, features: [] };
                        }
                    })
                );

                setFeatureGroups(groupsWithFeatures);
            } catch (err) {
                console.error('Failed to load reference data:', err);
                if (err.response?.status === 401) {
                    navigate('/login', {
                        state: { from: `/my-properties/${identifier}/edit`, message: 'Silakan login terlebih dahulu.' },
                    });
                }
            } finally {
                setLoadingData(false);
            }
        };

        fetchRefData();
    }, [navigate, identifier]);

    useEffect(() => {
        if (!identifier) return;

        const fetchProperty = async () => {
            setLoadingProperty(true);
            try {
                const res = await axiosInstance.get(`/properties/${identifier}`);
                if (!res.data?.success) throw new Error('Property not found');

                const data = res.data.data;
                setProperty(data);

                setForm({
                    ownerName: data.owner?.name || '',
                    ownerPhone: data.owner?.phone_number || '',
                    ownerEmail: data.owner?.email || '',
                    property_type_id: data.property_type_id ? String(data.property_type_id) : '',
                    certificate_type_id: data.certificate_type?.id ? String(data.certificate_type.id) : '',
                    status: data.status || '',
                    sale_rent_duration: data.sale_rent_duration || 'yearly',
                    title: data.title || '',
                    description: data.description || '',
                    address: data.address || '',
                    location: data.location || '',
                    province: data.province || '',
                    city: data.city || '',
                    district: data.district || '',
                    village: data.village || '',
                    latitude: data.latitude || '',
                    longitude: data.longitude || '',
                    land_area: data.land_area ? String(data.land_area) : '',
                    building_area: data.building_area ? String(data.building_area) : '',
                    bedrooms: data.bedrooms ? String(data.bedrooms) : '',
                    bathrooms: data.bathrooms ? String(data.bathrooms) : '',
                    floors: data.floors ? String(data.floors) : '',
                    construction_year: data.construction_year ? String(data.construction_year) : '',
                    renovation_year: data.renovation_year ? String(data.renovation_year) : '',
                    garage_count:
                        data.garage_count !== undefined && data.garage_count !== null ? String(data.garage_count) : '',
                    carport_count:
                        data.carport_count !== undefined && data.carport_count !== null
                            ? String(data.carport_count)
                            : '',
                    furnishing_status: data.furnishing_status || '',
                    electric_power: data.electric_power ? String(data.electric_power) : '',
                    building_orientation: data.building_orientation || '',
                    building_condition: data.building_condition || '',
                    price: data.price
                        ? new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(Number(data.price))
                        : '',
                    feature_ids: Array.isArray(data.features) ? data.features.map((f) => Number(f.id)) : [],
                    nearby_places: Array.isArray(data.nearby_places)
                        ? data.nearby_places.map((p) => ({
                            id: p.id,
                            category: p.category || '',
                            name: p.name || '',
                            address: p.address || '',
                            distance_km:
                                p.distance_km !== null && p.distance_km !== undefined ? String(p.distance_km) : '',
                            sort_order: p.sort_order ?? 0,
                        }))
                        : [],
                    property_specifications: data.specifications || [],
                });

                if (Array.isArray(data.images) && data.images.length > 0) {
                    const existingImages = data.images
                        .sort((a, b) => {
                            if (b.is_primary && !a.is_primary) return 1;
                            if (a.is_primary && !b.is_primary) return -1;
                            return (a.sort_order ?? 0) - (b.sort_order ?? 0);
                        })
                        .map((img) => ({
                            id: img.id,
                            preview: img.image_url || img.display_image_url,
                            isExisting: true,
                            toDelete: false,
                            isPrimary: img.is_primary,
                            sortOrder: img.sort_order,
                        }));
                    setPropertyImages(existingImages);
                }
            } catch (err) {
                console.error('Failed to load property:', err);
                if (err.response?.status === 401) {
                    navigate('/login', { state: { from: `/my-properties/${identifier}/edit` } });
                } else if (err.response?.status === 404 || err.response?.status === 403) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Tidak Ditemukan',
                        text: 'Properti tidak ditemukan atau Anda tidak memiliki akses.',
                    });
                    navigate('/my-properties');
                }
            } finally {
                setLoadingProperty(false);
            }
        };

        fetchProperty();
    }, [identifier, navigate]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    }, []);

    const handlePriceChange = useCallback((e) => {
        const raw = e.target.value;
        const formatted = formatRupiahInput(raw);
        setForm((prev) => ({ ...prev, price: formatted }));
        setErrors((prev) => ({ ...prev, price: '' }));
    }, []);

    const handleToggleFeature = useCallback((featureId) => {
        const normalizedId = Number(featureId);
        setForm((prev) => {
            const current = Array.isArray(prev.feature_ids) ? prev.feature_ids : [];
            const exists = current.some((id) => Number(id) === normalizedId);
            return {
                ...prev,
                feature_ids: exists ? current.filter((id) => Number(id) !== normalizedId) : [...current, normalizedId],
            };
        });
    }, []);

    const isFeatureSelected = useCallback(
        (featureId) => {
            const normalizedId = Number(featureId);
            const current = Array.isArray(form.feature_ids) ? form.feature_ids : [];
            return current.some((id) => Number(id) === normalizedId);
        },
        [form.feature_ids]
    );

    const addNearbyPlace = useCallback(() => {
        setForm((prev) => {
            const current = Array.isArray(prev.nearby_places) ? prev.nearby_places : [];
            return {
                ...prev,
                nearby_places: [...current, createEmptyNearbyPlace(current.length)],
            };
        });
    }, []);

    const updateNearbyPlace = useCallback((index, field, value) => {
        setForm((prev) => {
            const current = Array.isArray(prev.nearby_places) ? [...prev.nearby_places] : [];
            if (!current[index]) return prev;
            current[index] = { ...current[index], [field]: value };
            return { ...prev, nearby_places: current };
        });
    }, []);

    const removeNearbyPlace = useCallback((index) => {
        setForm((prev) => {
            const current = Array.isArray(prev.nearby_places) ? prev.nearby_places : [];
            const next = current.filter((_, i) => i !== index).map((item, idx) => ({ ...item, sort_order: idx }));
            return { ...prev, nearby_places: next };
        });
    }, []);

    const selectedPropertyType = useMemo(
        () => propertyTypes.find((t) => String(t.id) === String(form.property_type_id)),
        [propertyTypes, form.property_type_id]
    );

    const selectedCertificateType = useMemo(
        () => certificateTypes.find((t) => String(t.id) === String(form.certificate_type_id)),
        [certificateTypes, form.certificate_type_id]
    );

    const isTanah = Boolean(selectedPropertyType?.name?.toLowerCase().includes('tanah'));

    const nextStep = useCallback(() => {
        const e = validateStep(step, form);
        if (Object.keys(e).length > 0) {
            setErrors(e);
            return;
        }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [step, form]);

    const prevStep = useCallback(() => {
        setStep((s) => Math.max(s - 1, 0));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleSubmit = useCallback(async () => {
        const token = localStorage.getItem('mp_token');
        if (!token) {
            navigate('/login', { state: { from: `/my-properties/${identifier}/edit` } });
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            const payload = buildUpdatePayload(form, propertyImages);
            await axiosInstance.post(`/properties/${identifier}`, payload, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });


            setProperty((prev) => ({
                ...prev,
                ...form,
                price: form.price,
            }));

            setSubmitted(true);

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Properti berhasil diperbarui.',
                showConfirmButton: false,
                timer: 1500,
                timerProgressBar: true,
            });

        } catch (err) {
            console.error('Update error:', err);

            if (err.response?.status === 401) {
                localStorage.removeItem('mp_token');
                localStorage.removeItem('mp_user');
                navigate('/login', { state: { from: `/my-properties/${identifier}/edit` } });
                return;
            }

            const apiErrors = err?.response?.data?.errors;
            if (apiErrors) {
                const mapped = {};
                Object.entries(apiErrors).forEach(([key, msgs]) => {
                    mapped[key] = Array.isArray(msgs) ? msgs[0] : msgs;
                });
                setErrors(mapped);
            } else {
                setErrors({
                    general: err?.response?.data?.message || 'Terjadi kesalahan. Silakan coba lagi.',
                });
            }
        } finally {
            setLoading(false);
        }
    }, [form, propertyImages, navigate, identifier]);

    return (
        <EditPropertyContext.Provider
            value={{
                step,
                form,
                errors,
                loading,
                loadingProperty,
                submitted,
                property,
                identifier,
                propertyTypes,
                certificateTypes,
                featureGroups,
                loadingData,
                propertyImages,
                setPropertyImages,
                imageError,
                setImageError,
                handleChange,
                handlePriceChange,
                setFieldValue,
                handleToggleFeature,
                isFeatureSelected,
                addNearbyPlace,
                updateNearbyPlace,
                removeNearbyPlace,
                nextStep,
                prevStep,
                handleSubmit,
                selectedPropertyType,
                selectedCertificateType,
                isTanah,
            }}
        >
            {children}
        </EditPropertyContext.Provider>
    );
};
